import { useState, useEffect } from 'react';
import { useDataFactoryCalc } from '../../hooks/useCalculate';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useCalculatorStore } from '../../store/calculatorStore';
import { CurrencyTooltip } from '../shared/CurrencyTooltip';
import { FieldTooltip } from '../shared/FieldTooltip';
import { FormulaDisclosure } from '../shared/FormulaDisclosure';
import { DiscountModifier } from '../shared/DiscountModifier';
import { MobileCostBar } from '../shared/MobileCostBar';
import { FeedbackModal } from '../shared/FeedbackModal';
import { Disclaimer } from '../shared/Disclaimer';
import { ScenarioPresets } from '../shared/ScenarioPresets';
import type { DataFactoryRequest } from '../../api/types';

const defaultInputs: DataFactoryRequest = {
  copyActivity: { enabled: true, runsPerMonth: 100, avgDurationMinutes: 10, diuCount: 4 },
  dataFlow: { enabled: false, clusterType: 'general_purpose', vCores: 8, hoursPerMonth: 10 },
  selfHostedIR: { enabled: false, hoursPerMonth: 0 },
  orchestration: { activityRunsPerMonth: 500 },
};

const CLUSTER_LABELS: Record<string, string> = {
  general_purpose: 'General Purpose',
  memory_optimised: 'Memory Optimised',
  compute_optimised: 'Compute Optimised',
};

function NumberInput({
  label, value, min, max, step = 1, onChange, suffix,
}: {
  label: string; value: number; min: number; max?: number; step?: number;
  onChange: (v: number) => void; suffix?: string;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <label className="text-[11px] text-on-surface-variant">{label}</label>
      <div className="flex items-center gap-1">
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v)) onChange(Math.max(min, max !== undefined ? Math.min(max, v) : v));
          }}
          className="w-24 text-right bg-surface-container-high border border-outline-variant/40 rounded-sm px-2 py-1 text-[12px] text-on-surface"
        />
        {suffix && <span className="text-[10px] text-outline">{suffix}</span>}
      </div>
    </div>
  );
}

function Toggle({ enabled, onToggle, label }: { enabled: boolean; onToggle: () => void; label: string }) {
  return (
    <button
      onClick={onToggle}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-sm border text-[11px] font-bold uppercase tracking-widest transition-colors ${
        enabled
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-outline-variant/40 text-outline hover:border-outline'
      }`}
    >
      <span className="material-symbols-outlined text-[14px]">
        {enabled ? 'toggle_on' : 'toggle_off'}
      </span>
      {label}
    </button>
  );
}

export default function DataFactoryCalc() {
  const [inputs, setInputs] = useState<DataFactoryRequest>(defaultInputs);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { discount, setDiscount } = useCalculatorStore();

  const { data, isLoading, isError } = useDataFactoryCalc(inputs);
  const { trackCalculatorUsed } = useAnalytics();
  useEffect(() => {
    if (data?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'data-factory', result_gbp: data.monthly_estimate })
    }
  }, [data?.monthly_estimate])

  function set<K extends keyof DataFactoryRequest>(section: K, partial: Partial<DataFactoryRequest[K]>) {
    setInputs((prev) => ({ ...prev, [section]: { ...prev[section], ...partial } }));
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 items-start">
      {/* ── Inputs ── */}
      <div className="flex flex-col gap-4">

        <ScenarioPresets presets={[
          {
            label: 'Daily ETL pipeline',
            description: 'Copy Activity · 4 DIU · 30 min · 1 run/day — most common ADF pattern',
            apply: () => setInputs(prev => ({ ...prev, copyActivity: { ...prev.copyActivity, enabled: true, runsPerMonth: 30, avgDurationMinutes: 30, diuCount: 4 }, dataFlow: { ...prev.dataFlow, enabled: false } })),
          },
          {
            label: 'Hourly ingestion',
            description: 'Copy Activity · 4 DIU · 5 min · 24 runs/day — near-real-time pattern',
            apply: () => setInputs(prev => ({ ...prev, copyActivity: { ...prev.copyActivity, enabled: true, runsPerMonth: 720, avgDurationMinutes: 5, diuCount: 4 }, dataFlow: { ...prev.dataFlow, enabled: false } })),
          },
          {
            label: 'Data Flow transformation',
            description: 'Data Flow · General Purpose · 8 vCores · 15 min · 4 runs/day',
            apply: () => setInputs(prev => ({ ...prev, copyActivity: { ...prev.copyActivity, enabled: false }, dataFlow: { ...prev.dataFlow, enabled: true, clusterType: 'general_purpose', vCores: 8, hoursPerMonth: 30 } })),
          },
        ]} />

        {/* Section 1: Copy Activity */}
        <div className="bg-surface-container-high border border-outline-variant/30 rounded-lg p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[13px] font-bold text-on-surface uppercase tracking-widest">Copy Activity</h2>
              <p className="text-[10px] text-outline mt-0.5">Azure-hosted IR · charged per DIU-hour</p>
            </div>
            <Toggle
              enabled={inputs.copyActivity.enabled}
              onToggle={() => set('copyActivity', { enabled: !inputs.copyActivity.enabled })}
              label={inputs.copyActivity.enabled ? 'Enabled' : 'Disabled'}
            />
          </div>

          {inputs.copyActivity.enabled && (
            <div className="flex flex-col gap-3">
              <NumberInput
                label="Pipeline runs / month"
                value={inputs.copyActivity.runsPerMonth}
                min={0}
                onChange={(v) => set('copyActivity', { runsPerMonth: v })}
                suffix="runs"
              />
              <NumberInput
                label="Avg. duration / run"
                value={inputs.copyActivity.avgDurationMinutes}
                min={0.1}
                max={1440}
                step={0.5}
                onChange={(v) => set('copyActivity', { avgDurationMinutes: v })}
                suffix="min"
              />
              <div className="flex items-center justify-between gap-3">
                <label className="text-[11px] text-on-surface-variant">DIU count <FieldTooltip content="Data Integration Units — measure of CPU, memory, and network for copy operations. Higher DIU = faster copy but higher cost." /></label>
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={2}
                    max={32}
                    step={2}
                    value={inputs.copyActivity.diuCount}
                    onChange={(e) => set('copyActivity', { diuCount: parseInt(e.target.value, 10) })}
                    className="w-24 accent-primary"
                  />
                  <span className="text-[12px] font-mono text-on-surface w-8 text-right">
                    {inputs.copyActivity.diuCount}
                  </span>
                </div>
              </div>

              {inputs.copyActivity.diuCount === 4 && (
                <div className="bg-[#107c10]/10 border border-[#107c10]/40 p-3 flex gap-2 items-start rounded-sm">
                  <span className="material-symbols-outlined text-[#107c10] text-[16px] shrink-0">lightbulb</span>
                  <p className="text-[10px] text-on-surface leading-relaxed">
                    <strong className="text-[#107c10]">Cost tip:</strong> The "Auto" DIU setting defaults to 4 DIUs minimum.
                    For dev/test pipelines, manually setting 2 DIUs halves your Copy Activity cost.
                    Production workloads benefit from higher DIU counts for parallelism.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Section 2: Data Flow */}
        <div className="bg-surface-container-high border border-outline-variant/30 rounded-lg p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[13px] font-bold text-on-surface uppercase tracking-widest">Data Flow</h2>
              <p className="text-[10px] text-outline mt-0.5">Compute-intensive transformations · charged per vCore-hour</p>
            </div>
            <Toggle
              enabled={inputs.dataFlow.enabled}
              onToggle={() => set('dataFlow', { enabled: !inputs.dataFlow.enabled })}
              label={inputs.dataFlow.enabled ? 'Enabled' : 'Disabled'}
            />
          </div>

          {inputs.dataFlow.enabled && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-3">
                <label className="text-[11px] text-on-surface-variant">Cluster type <FieldTooltip content="General Purpose: balanced cost/performance. Memory Optimised: data-heavy transforms. Compute Optimised: CPU-intensive flows." /></label>
                <select
                  value={inputs.dataFlow.clusterType}
                  onChange={(e) =>
                    set('dataFlow', {
                      clusterType: e.target.value as DataFactoryRequest['dataFlow']['clusterType'],
                    })
                  }
                  className="bg-surface-container-high border border-outline-variant/40 rounded-sm px-2 py-1 text-[12px] text-on-surface"
                >
                  {Object.entries(CLUSTER_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <NumberInput
                label="vCores"
                value={inputs.dataFlow.vCores}
                min={8}
                max={512}
                step={8}
                onChange={(v) => set('dataFlow', { vCores: v })}
                suffix="cores"
              />
              <NumberInput
                label="Hours / month"
                value={inputs.dataFlow.hoursPerMonth}
                min={0}
                onChange={(v) => set('dataFlow', { hoursPerMonth: v })}
                suffix="hr"
              />
              <p className="text-[10px] text-outline italic">
                Data Flow rates sourced from Azure pricing page — not in the Retail Prices API.
              </p>
            </div>
          )}
        </div>

        {/* Section 3: Self-hosted IR */}
        <div className="bg-surface-container-high border border-outline-variant/30 rounded-lg p-5 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-[13px] font-bold text-on-surface uppercase tracking-widest">Self-hosted IR</h2>
              <p className="text-[10px] text-outline mt-0.5">On-premises data movement · charged per hour</p>
            </div>
            <Toggle
              enabled={inputs.selfHostedIR.enabled}
              onToggle={() => set('selfHostedIR', { enabled: !inputs.selfHostedIR.enabled })}
              label={inputs.selfHostedIR.enabled ? 'Enabled' : 'Disabled'}
            />
          </div>

          {inputs.selfHostedIR.enabled && (
            <NumberInput
              label="Hours / month"
              value={inputs.selfHostedIR.hoursPerMonth}
              min={0}
              max={744}
              onChange={(v) => set('selfHostedIR', { hoursPerMonth: v })}
              suffix="hr"
            />
          )}
        </div>

        {/* Section 4: Orchestration */}
        <div className="bg-surface-container-high border border-outline-variant/30 rounded-lg p-5 flex flex-col gap-4">
          <div>
            <h2 className="text-[13px] font-bold text-on-surface uppercase tracking-widest">Pipeline Orchestration</h2>
            <p className="text-[10px] text-outline mt-0.5">Activity runs · first 1,000/month free (Azure-hosted IR)</p>
          </div>
          <NumberInput
            label="Activity runs / month"
            value={inputs.orchestration.activityRunsPerMonth}
            min={0}
            onChange={(v) => set('orchestration', { activityRunsPerMonth: v })}
            suffix="runs"
          />
          {inputs.orchestration.activityRunsPerMonth <= 1000 && (
            <p className="text-[10px] text-[#107c10]">
              ✓ Within free 1,000 runs/month — no orchestration charge
            </p>
          )}
        </div>

      </div>

      {/* ── Results ── */}
      <div className="flex flex-col gap-4">
        <div id="adf-results" className="bg-surface-container-high border-l-2 border-primary rounded-lg overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-outline-variant/20">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-outline">Monthly estimate (PAYG)</p>
              <p className="text-3xl font-mono font-bold text-on-surface mt-0.5">
                {isLoading ? '...' : isError ? 'Error' : `£${data?.monthly_estimate?.toFixed(2) ?? '0.00'}`}
              </p>
            </div>
            <CurrencyTooltip />
          </div>

          {/* Breakdown */}
          {data && !isLoading && !isError && (
            <div className="px-6 py-4 flex flex-col gap-2">
              {data.breakdown.map((item, i) => (
                <div key={i} className="flex justify-between items-start gap-2">
                  <span className="text-[11px] text-on-surface-variant leading-snug">{item.label}</span>
                  <span className={`text-[11px] font-mono shrink-0 ${item.isFree ? 'text-[#107c10]' : 'text-on-surface'}`}>
                    {item.isFree ? 'FREE' : `£${item.value.toFixed(2)}`}
                  </span>
                </div>
              ))}

              {data.breakdown.length === 0 && (
                <p className="text-[11px] text-outline italic">Enable at least one component above.</p>
              )}
            </div>
          )}

          {/* Formula */}
          {data && (
            <div className="px-6 pb-4">
              <FormulaDisclosure
                lines={[
                  ...(data.breakdown ?? []).map((item) =>
                    `${item.label}: ${item.isFree ? 'FREE' : `£${item.value.toFixed(4)}`}`
                  ),
                  `Region: UK South`,
                ]}
                fetchedAt={data.prices_last_updated}
              />
            </div>
          )}
        </div>

        {/* DIU tip card */}
        {data?.showDiuTip && (
          <div className="bg-[#107c10]/10 border border-[#107c10]/40 p-4 rounded-sm">
            <p className="text-[11px] text-on-surface leading-relaxed">
              <strong className="text-[#107c10]">Halve your Copy Activity cost:</strong>{' '}
              Setting DIU count to 2 instead of 4 reduces Copy Activity cost from{' '}
              £{(data.breakdown_detail.copyActivity.cost).toFixed(2)} to approximately{' '}
              £{(data.breakdown_detail.copyActivity.cost / 2).toFixed(2)}/month for dev/test pipelines.
            </p>
          </div>
        )}

        {/* Discount */}
        {data && (
          <DiscountModifier
            discount={discount}
            onDiscountChange={setDiscount}
            paygTotal={data.monthly_estimate}
          />
        )}

        {/* Disclaimer */}
        <div className="bg-surface-container-lowest/50 border border-outline-variant p-4 text-[10px] text-outline leading-relaxed">
          <Disclaimer />
          <p className="mt-1 italic">{data?.disclaimer}</p>
        </div>

        <a href="https://azure.microsoft.com/en-gb/pricing/details/data-factory/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-mono" style={{ color: 'var(--text-tertiary)' }}>
          <span className="material-symbols-outlined text-[13px]">open_in_new</span>
          Verify on official Azure pricing page
        </a>

        {/* Feedback */}
        <button
          onClick={() => setFeedbackOpen(true)}
          className="text-[10px] text-outline hover:text-on-surface-variant transition-colors flex items-center gap-1"
        >
          <span className="material-symbols-outlined text-[14px]">feedback</span>
          Report a pricing issue
        </button>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          service="data-factory"
          estimateValue={data ? `£${data.monthly_estimate?.toFixed(2)}/mo` : 'N/A'}
          onClose={() => setFeedbackOpen(false)}
        />
      )}

      <MobileCostBar
        amount={data?.monthly_estimate ?? null}
        label="Data Factory"
        targetId="adf-results"
      />
    </div>
  );
}
